import { logInfo, logWarn, logError } from '../utils/logger';
import { api } from './api';

/**
 * SAP Datasphere connection service — OAuth 2.0 Authorization Code flow.
 *
 * Flow (FI-034):
 *   1. Call initiateConnection() → backend returns SAP authorize URL.
 *   2. Browser is redirected to SAP; user authenticates.
 *   3. SAP redirects to /sap/callback?code=...&state=...
 *   4. SapCallback.tsx page calls exchangeCode(code, state) → backend
 *      exchanges the code through SapTokenProvider and upserts datasource row.
 *   5. Frontend navigates back to the surface that started the flow.
 *
 * checkConnectionStatus() still reads the DB-backed ack (datasources table)
 * and is called by the DataSourceModal to drive the "connected" UI state.
 */

type ConnectionStatusResponse = {
  connected?: boolean;
  connected_at?: string | null;
  message?: string;
};

type ExchangeCodeOptions = {
  requireConnectedAck?: boolean;
};

type AuthorizeUrlResponse = {
  authorization_url: string;
  state: string;
};

const SAP_OAUTH_RETURN_ROUTE_KEY = 'smartchg:sap-oauth:return-route';
const DEFAULT_SAP_RETURN_ROUTE = '/smart-insights';
const SAP_SOURCE_RETURN_ROUTES: Record<string, string> = {
  'finance-v2': '/smart-insights',
  allocations: '/allocations',
  'labor-performance': '/labor-performance',
  'smart-morning-for-leaders': '/smart-morning-for-leaders',
};
const ALLOWED_SAP_RETURN_ROUTES = new Set(Object.values(SAP_SOURCE_RETURN_ROUTES));

function resolveSapReturnRoute(source: string): string {
  const normalizedSource = source.trim();
  const mappedRoute = SAP_SOURCE_RETURN_ROUTES[normalizedSource];
  if (mappedRoute) return mappedRoute;
  if (ALLOWED_SAP_RETURN_ROUTES.has(normalizedSource)) return normalizedSource;
  return DEFAULT_SAP_RETURN_ROUTE;
}

export function getSapOAuthReturnRoute(): string {
  try {
    const storedRoute = window.sessionStorage.getItem(SAP_OAUTH_RETURN_ROUTE_KEY);
    if (storedRoute && ALLOWED_SAP_RETURN_ROUTES.has(storedRoute)) {
      return storedRoute;
    }
  } catch {
    // Storage may be unavailable in hardened browser contexts; fall back safely.
  }
  return DEFAULT_SAP_RETURN_ROUTE;
}

export function clearSapOAuthReturnRoute(): void {
  try {
    window.sessionStorage.removeItem(SAP_OAUTH_RETURN_ROUTE_KEY);
  } catch {
    // No-op when sessionStorage is unavailable.
  }
}

function storeSapOAuthReturnRoute(source: string): void {
  try {
    window.sessionStorage.setItem(SAP_OAUTH_RETURN_ROUTE_KEY, resolveSapReturnRoute(source));
  } catch {
    // No-op when sessionStorage is unavailable; callback will use the default.
  }
}

class SapOAuthService {
  /**
   * Start the SAP OAuth flow:
   *   GET /auth/sap/authorize-url → redirect the browser to the returned URL.
   * SAP will redirect back to /sap/callback with code + state.
   */
  async initiateConnection(source: string = 'finance-v2'): Promise<void> {
    try {
      logInfo('SAP OAuth', 'Requesting SAP authorize URL');
      const response = await api.get<AuthorizeUrlResponse>('/auth/sap/authorize-url');
      const url = response.data?.authorization_url;
      if (!url) {
        throw new Error('Backend did not return an SAP authorization URL');
      }
      storeSapOAuthReturnRoute(source);
      logInfo('SAP OAuth', 'Redirecting to SAP for authentication');
      window.location.href = url;
    } catch (error: unknown) {
      logError(
        'SAP OAuth',
        'Failed to initiate SAP connection',
        error instanceof Error ? error : new Error('Unknown error')
      );
      throw error;
    }
  }

  /**
   * Exchange the SAP authorization code for an access token.
   * Called by SapCallback.tsx after SAP redirects with ?code=...&state=...
   */
  async exchangeCode(code: string, state: string, options: ExchangeCodeOptions = {}): Promise<void> {
    logInfo('SAP OAuth', 'Exchanging SAP authorization code');
    const response = await api.post<ConnectionStatusResponse>('/auth/sap/callback-exchange', {
      code,
      state,
    });
    const requireConnectedAck = options.requireConnectedAck ?? true;
    if (requireConnectedAck && !response.data?.connected) {
      throw new Error(response.data?.message || 'Backend did not confirm SAP connection after code exchange');
    }
    logInfo('SAP OAuth', 'SAP code exchange successful');
  }

  /** Read the backend's DB-backed connection ack (datasources table). */
  async checkConnectionStatus(): Promise<{ connected: boolean; message?: string }> {
    try {
      const response = await api.get<ConnectionStatusResponse>('/auth/sap/connection-status');
      return {
        connected: response.data?.connected || false,
        message: response.data?.message,
      };
    } catch (error: unknown) {
      logWarn(
        'SAP OAuth',
        'Failed to check SAP connection status',
        error instanceof Error ? error : new Error('Unknown error')
      );
      return { connected: false };
    }
  }
}

export const sapOAuthService = new SapOAuthService();
